import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  HStack,
  VStack,
  Icon,
  Button,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  FormControl,
  FormLabel,
  useColorModeValue,
  Collapse,
  useDisclosure,
  Switch,
  Tooltip,
  InputGroup,
  InputRightAddon,
  Divider,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FaBox, FaSitemap, FaStore, FaUnlink, FaLink } from 'react-icons/fa';
import {
  getGatewayMeta,
  resolveLogicalVariantKey,
  countMappedVariants,
} from '../utils/productVariantUtils';

const LEFT_COL_W = { base: '180px', md: '230px' };
const COL_GAP = 3;
const MATRIX_MAX_HEIGHT = { base: '55vh', md: '65vh', xl: '70vh' };

function ConnectorDown({ height = 24 }) {
  const color = useColorModeValue('gray.300', 'whiteAlpha.400');
  return <Box w="2px" h={`${height}px`} bg={color} mx="auto" />;
}

function ConnectorBus({ childCount }) {
  const color = useColorModeValue('gray.300', 'whiteAlpha.400');
  if (childCount <= 1) return <ConnectorDown height={20} />;
  return (
    <Box position="relative" w="full" h="28px" px={4}>
      <Box position="absolute" top={0} left="50%" w="2px" h="14px" bg={color} transform="translateX(-50%)" />
      <Box
        position="absolute"
        top="14px"
        left={`${100 / (childCount * 2)}%`}
        right={`${100 / (childCount * 2)}%`}
        h="2px"
        bg={color}
      />
      <Flex justify="space-around" position="absolute" top="14px" left={0} right={0}>
        {Array.from({ length: childCount }).map((_, i) => (
          <Box key={i} w="2px" h="14px" bg={color} />
        ))}
      </Flex>
    </Box>
  );
}

function StockPill({ stock, isEditMode }) {
  const color = stock > 10 ? 'green' : stock > 0 ? 'orange' : 'red';
  return (
    <Badge colorScheme={color} fontSize="10px" px={2} borderRadius="full">
      {isEditMode ? `${stock ?? 0} انبار` : `${stock ?? 0}`}
    </Badge>
  );
}

/** درصد خالی یعنی «تعیین نشده»؛ تنوع بدون درصد از درصد درگاه پیروی می‌کند */
function optionalPercent(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * PriceInput — فیلد قیمت با InputGroup و addon «ت»
 * برای قیمت‌های ایرانی که نیاز به عرض بیشتر دارند
 */
function PriceInput({ value, onChange, placeholder = 'قیمت', isDisabled = false, bg }) {
  const defaultBg = useColorModeValue('gray.50', 'gray.900');
  const addonBg = useColorModeValue('gray.100', 'gray.700');
  const addonColor = useColorModeValue('gray.500', 'gray.400');
  return (
    <InputGroup size="xs" flex={1} minW="90px">
      <NumberInput
        size="xs"
        min={0}
        flex={1}
        value={value ?? 0}
        isDisabled={isDisabled}
        onChange={(_, n) => onChange(Number.isFinite(n) ? n : 0)}
        w="100%"
      >
        <NumberInputField
          placeholder={placeholder}
          bg={bg || defaultBg}
          borderRadius="md"
          borderRightRadius={0}
          dir="ltr"
          fontSize="xs"
          textAlign="left"
          px={2}
        />
      </NumberInput>
      <InputRightAddon
        fontSize="9px"
        px={1.5}
        bg={addonBg}
        color={addonColor}
      >
        ت
      </InputRightAddon>
    </InputGroup>
  );
}

function GatewayHeader({
  refIndex,
  gatewayRef,
  gateways,
  onGatewayRefField,
  onRemoveGatewayRef,
  columnWidth,
  isProductSyncDisabled = false,
}) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false });
  const meta = getGatewayMeta(gatewayRef, gateways);

  const gwId = gatewayRef?.gateway?._id || gatewayRef?.gateway;
  const matchedGateway = gateways?.find((g) => String(g._id) === String(gwId));
  const isGlobalDisabled = matchedGateway?.syncPrice === false || gatewayRef?.gateway?.syncPrice === false;
  const isSyncDisabled = isProductSyncDisabled || isGlobalDisabled || gatewayRef?.syncPrice === false;

  const headerBg = useColorModeValue(`${meta.color}.50`, `${meta.color}.900`);
  const borderColor = useColorModeValue(`${meta.color}.200`, `${meta.color}.700`);
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const panelBg = useColorModeValue('white', 'gray.800');

  return (
    <Box
      minW={columnWidth}
      maxW={columnWidth}
      flexShrink={0}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      bg={headerBg}
      boxShadow="sm"
    >
      <Flex
        p={2.5}
        align="center"
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ opacity: 0.9 }}
      >
        <Box flex={1} minW={0}>
          <HStack spacing={1} mb={1} wrap="wrap">
            <Badge colorScheme={meta.color} fontSize="9px" borderRadius="md">
              {meta.type}
            </Badge>
            {isProductSyncDisabled ? (
              <Badge colorScheme="purple" variant="solid" fontSize="8px" borderRadius="md" title="سیستم قیمت متناسب برای این محصول غیرفعال است">
                محصول غیرفعال
              </Badge>
            ) : isGlobalDisabled ? (
              <Badge colorScheme="purple" variant="solid" fontSize="8px" borderRadius="md" title="قیمت‌گذاری این درگاه به طور همگانی در تنظیمات درگاه غیرفعال است">
                قیمت‌گذاری همگانی غیرفعال
              </Badge>
            ) : gatewayRef?.syncPrice === false ? (
              <Badge colorScheme="purple" variant="subtle" fontSize="8px" borderRadius="md" title="قیمت‌گذاری این درگاه روی این محصول غیرفعال است">
                قیمت مستقل
              </Badge>
            ) : null}
          </HStack>
          <Text fontSize="xs" fontWeight="bold" color={textColor} isTruncated title={meta.name}>
            {meta.name}
          </Text>
          {gatewayRef?.productId && (
            <Text fontSize="9px" color={mutedColor} dir="ltr" isTruncated>
              PID: {gatewayRef.productId}
            </Text>
          )}
        </Box>
        <Icon as={isOpen ? ChevronUpIcon : ChevronDownIcon} boxSize={3.5} color={mutedColor} />
      </Flex>
      <Collapse in={isOpen}>
        <VStack align="stretch" p={2.5} pt={0} spacing={2} bg={panelBg} borderTopWidth="1px" borderColor={borderColor}>
          <FormControl>
            <FormLabel fontSize="9px" mb={0} color={mutedColor}>درگاه</FormLabel>
            <Select
              size="xs"
              borderRadius="md"
              value={gatewayRef?.gateway || ''}
              onChange={(e) => onGatewayRefField(refIndex, 'gateway', e.target.value)}
            >
              <option value="">انتخاب...</option>
              {gateways.map((g) => (
                <option key={g._id} value={g._id}>{g.name}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="9px" mb={0} color={mutedColor}>کد محصول (PID)</FormLabel>
            <Input
              size="xs"
              borderRadius="md"
              dir="ltr"
              value={gatewayRef?.productId || ''}
              onChange={(e) => onGatewayRefField(refIndex, 'productId', e.target.value)}
            />
          </FormControl>
          <FormControl display="flex" alignItems="center" justifyContent="space-between" pt={1}>
            <Tooltip
              label="وقتی فعال است، قیمت تنوع‌های این درگاه بر اساس درصد از قیمت پایه محاسبه می‌شود"
              fontSize="xs"
              placement="top"
              hasArrow
            >
              <FormLabel fontSize="9px" mb={0} color={mutedColor} cursor="help" textDecoration="underline dotted">
                {isProductSyncDisabled ? 'قیمت‌گذاری این محصول در این درگاه (غیرفعال در محصول)' : isGlobalDisabled ? 'قیمت‌گذاری این محصول در این درگاه (غیرفعال در درگاه)' : 'قیمت‌گذاری این محصول در این درگاه'}
              </FormLabel>
            </Tooltip>
            <Switch
              size="xs"
              colorScheme="teal"
              isChecked={!isSyncDisabled}
              isDisabled={isGlobalDisabled || isProductSyncDisabled}
              onChange={(e) => onGatewayRefField(refIndex, 'syncPrice', e.target.checked)}
            />
          </FormControl>
          {!isSyncDisabled && (
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Tooltip
                label="خالی یا ۰ = درصد همگانی خود درگاه؛ در غیر این صورت برای تنوع‌هایی که درصد جدا ندارند از این مقدار استفاده می‌شود"
                fontSize="xs"
                placement="top"
                hasArrow
              >
                <FormLabel fontSize="9px" mb={0} color={mutedColor} cursor="help" textDecoration="underline dotted">
                  درصد این محصول (%)
                </FormLabel>
              </Tooltip>
              <NumberInput
                size="xs"
                w="80px"
                value={gatewayRef?.pricePercent == null || gatewayRef?.pricePercent === '' || Number(gatewayRef?.pricePercent) === 0 ? '' : gatewayRef.pricePercent}
                onChange={(str, n) =>
                  onGatewayRefField(
                    refIndex,
                    'pricePercent',
                    str === '' || !Number.isFinite(n) || n === 0 ? null : n
                  )
                }
              >
                <NumberInputField
                  borderRadius="md"
                  dir="ltr"
                  fontSize="xs"
                  textAlign="center"
                  px={2}
                  placeholder={String(matchedGateway?.pricePercent ?? 0)}
                  title="خالی = درصد همگانی درگاه"
                />
              </NumberInput>
            </FormControl>
          )}
          <Button
            size="xs"
            colorScheme="red"
            variant="ghost"
            leftIcon={<DeleteIcon boxSize={2.5} />}
            onClick={() => onRemoveGatewayRef(refIndex)}
          >
            حذف درگاه
          </Button>
        </VStack>
      </Collapse>
    </Box>
  );
}

function VariantCellCard({
  variant,
  refIndex,
  variantIndex,
  gwColor,
  isEditMode,
  onUpdateVariant,
  onRemoveVariant,
  basePrice = 0,
  gatewayPercent = 0,
  isGatewaySyncDisabled = false,
  isProductSyncDisabled = false,
  isLogicalVariantSyncDisabled = false,
}) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('gray.50', 'gray.900');
  const targetBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const isVariantSyncDisabled = variant.syncPrice === false;
  const isSyncDisabled = isProductSyncDisabled || isGatewaySyncDisabled || isLogicalVariantSyncDisabled || isVariantSyncDisabled;

  const base = Number(basePrice) || 0;
  const variantPercent = optionalPercent(variant.pricePercent);
  const percent = variantPercent ?? optionalPercent(gatewayPercent) ?? 0;
  const targetPrice = (!isSyncDisabled && base > 0) ? Math.round(base * (1 + percent / 100)) : null;
  const currentPrice = Number(variant.price) || 0;
  const isMatch = isSyncDisabled ? true : (targetPrice != null ? currentPrice === targetPrice : true);

  const handleApplyTargetPrice = () => {
    if (targetPrice != null && !isSyncDisabled) {
      onUpdateVariant(refIndex, variantIndex, 'price', targetPrice);
    }
  };

  return (
    <Box
      p={2}
      bg={cardBg}
      borderWidth="1px"
      borderColor={!isMatch ? 'orange.300' : borderColor}
      borderRadius="lg"
      borderTopWidth="3px"
      borderTopColor={`${gwColor}.400`}
      fontSize="xs"
      boxShadow="2xs"
    >
      <Flex justify="space-between" align="start" mb={1}>
        <HStack spacing={1}>
          <StockPill stock={variant.stock} isEditMode={isEditMode} />
          {isProductSyncDisabled ? (
            <Badge colorScheme="purple" variant="solid" fontSize="8px" borderRadius="md" title="تبدیل قیمت برای کل این محصول غیرفعال است">
              محصول غیرفعال
            </Badge>
          ) : isGatewaySyncDisabled ? (
            <Badge colorScheme="purple" variant="solid" fontSize="8px" borderRadius="md" title="توسط تنظیمات درگاه غیرفعال شده است">
              درگاه غیرفعال
            </Badge>
          ) : isLogicalVariantSyncDisabled ? (
            <Badge colorScheme="purple" variant="solid" fontSize="8px" borderRadius="md" title="قیمت‌گذاری این تنوع منطقی برای همه درگاه‌ها غیرفعال است">
              تنوع منطقی غیرفعال
            </Badge>
          ) : isVariantSyncDisabled ? (
            <Badge colorScheme="purple" variant="subtle" fontSize="8px" borderRadius="md" title="عدم تبدیل و اعمال قیمت روی این تنوع">
              قیمت مستقل
            </Badge>
          ) : (
            targetPrice != null && (
              <Badge colorScheme={isMatch ? 'green' : 'orange'} fontSize="8px" borderRadius="md">
                {isMatch ? 'هماهنگ' : (
                  <Tooltip label="قیمت فعلی با قیمت محاسبه‌شده (پایه × درصد) مغایرت دارد" fontSize="xs" placement="top" hasArrow>
                    <span>مغایرت قیمت</span>
                  </Tooltip>
                )}
              </Badge>
            )
          )}
        </HStack>
        <IconButton
          aria-label="حذف"
          icon={<DeleteIcon />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          onClick={() => onRemoveVariant(refIndex, variantIndex)}
        />
      </Flex>
      <VStack spacing={2} align="stretch">
        <Input
          size="xs"
          placeholder="Variant ID"
          dir="ltr"
          borderRadius="md"
          bg={inputBg}
          value={variant.variantId || ''}
          onChange={(e) => onUpdateVariant(refIndex, variantIndex, 'variantId', e.target.value)}
        />
        <Input
          size="xs"
          placeholder="SKU"
          dir="ltr"
          borderRadius="md"
          bg={inputBg}
          value={variant.sku || ''}
          onChange={(e) => onUpdateVariant(refIndex, variantIndex, 'sku', e.target.value)}
        />
        <Divider opacity="0.4" />
        <Flex
          align="center"
          justify="space-between"
          gap={1}
        >
          <Tooltip
            label={
              isProductSyncDisabled
                ? 'توسط تنظیمات کل این محصول غیرفعال شده است'
                : isGatewaySyncDisabled
                ? 'توسط تنظیمات درگاه یا این محصول در این درگاه غیرفعال شده است'
                : isLogicalVariantSyncDisabled
                ? 'قیمت‌گذاری این تنوع منطقی برای همه درگاه‌ها غیرفعال است'
                : 'خاموش = قیمت این تنوع در این درگاه دست‌نخورده می‌ماند'
            }
            fontSize="xs"
            placement="top"
            hasArrow
          >
            <Text fontSize="9px" fontWeight="semibold" color={mutedColor} whiteSpace="nowrap" cursor="help" textDecoration="underline dotted">قیمت این تنوع در این درگاه:</Text>
          </Tooltip>
          <Switch
            size="xs"
            colorScheme="teal"
            isChecked={!isVariantSyncDisabled && !isGatewaySyncDisabled && !isProductSyncDisabled && !isLogicalVariantSyncDisabled}
            isDisabled={isGatewaySyncDisabled || isProductSyncDisabled || isLogicalVariantSyncDisabled}
            onChange={(e) => onUpdateVariant(refIndex, variantIndex, 'syncPrice', e.target.checked)}
          />
        </Flex>
        {!isSyncDisabled && (
          <>
            <Flex align="center" justify="space-between" gap={1}>
              <Tooltip
                label="خالی = از درصد این محصول یا درصد همگانی درگاه استفاده می‌شود"
                fontSize="xs"
                placement="top"
                hasArrow
              >
                <Text fontSize="9px" fontWeight="semibold" color={mutedColor} whiteSpace="nowrap" cursor="help" textDecoration="underline dotted">درصد (%):</Text>
              </Tooltip>
              <NumberInput
                size="xs"
                w="80px"
                value={variantPercent ?? ''}
                onChange={(str, n) =>
                  onUpdateVariant(refIndex, variantIndex, 'pricePercent', str === '' || !Number.isFinite(n) ? null : n)
                }
              >
                <NumberInputField
                  placeholder={String(optionalPercent(gatewayPercent) ?? 0)}
                  title="خالی = درصد محصول یا درگاه"
                  bg={inputBg}
                  borderRadius="md"
                  dir="ltr"
                  fontSize="xs"
                  textAlign="center"
                  paddingX="2"
                />
              </NumberInput>
            </Flex>
            {targetPrice != null && (
              <Flex align="center" justify="space-between" bg={targetBg} p={1} borderRadius="md">
                <Text fontSize="9px" color={mutedColor}>هدف:</Text>
                <HStack spacing={1}>
                  <Text fontSize="9px" fontWeight="bold" color={isMatch ? 'green.600' : 'orange.600'} dir="ltr">
                    {targetPrice.toLocaleString('fa-IR')}
                  </Text>
                  {!isMatch && (
                    <Button size="xs" colorScheme="orange" variant="solid" onClick={handleApplyTargetPrice}>
                      اصلاح
                    </Button>
                  )}
                </HStack>
              </Flex>
            )}
          </>
        )}
        <Flex align="center" justify="space-between" gap={1}>
          <Text fontSize="9px" fontWeight="semibold" color={mutedColor} whiteSpace="nowrap">قیمت فعلی:</Text>
          <PriceInput
            value={variant.price}
            onChange={(n) => onUpdateVariant(refIndex, variantIndex, 'price', n)}
            placeholder="قیمت"
            bg={inputBg}
          />
        </Flex>
      </VStack>
      {variant.name && (
        <Text fontSize="9px" color={mutedColor} mt={1} isTruncated title={variant.name}>
          {variant.name}
        </Text>
      )}
    </Box>
  );
}

function getVariantsInCellForRow(variants, logicalKey, row) {
  const list = variants || [];
  if (!row) return [];
  if (row.isProductLevel) return list;
  if (row.isUnmappedRow) return list.filter((v) => !v.logicalVariantId);
  return list.filter((v) => String(v.logicalVariantId) === String(logicalKey));
}

function MatrixCell({
  refIndex,
  gatewayRef,
  logicalKey,
  row,
  gwColor,
  isEditMode,
  onAddVariant,
  onUpdateVariant,
  onRemoveVariant,
  columnWidth,
  gateways,
  isProductSyncDisabled = false,
}) {
  const emptyBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const emptyBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedColor = useColorModeValue('gray.400', 'gray.500');

  if (!gatewayRef || !row) return null;

  const gwId = gatewayRef?.gateway?._id || gatewayRef?.gateway;
  const matchedGateway = gateways?.find((g) => String(g._id) === String(gwId));
  const isGlobalDisabled = matchedGateway?.syncPrice === false || gatewayRef?.gateway?.syncPrice === false;
  const isGatewaySyncDisabled = isGlobalDisabled || gatewayRef.syncPrice === false;
  const isLogicalVariantSyncDisabled = !row.isUnmappedRow && !row.isProductLevel && row.lv?.syncPrice === false;

  const cellVariants = getVariantsInCellForRow(gatewayRef.variants, logicalKey, row);

  return (
    <Box
      minW={columnWidth}
      maxW={columnWidth}
      flexShrink={0}
      minH="85px"
      alignSelf="stretch"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      p={1.5}
      borderWidth="1px"
      borderColor={emptyBorder}
      borderRadius="xl"
      bg={cellVariants.length ? 'transparent' : emptyBg}
      borderStyle={cellVariants.length ? 'solid' : 'dashed'}
      transition="all 0.2s ease"
    >
      {cellVariants.length > 0 ? (
        <VStack spacing={2} align="stretch" h="full" justify="center">
          {cellVariants.map((v) => {
            const variantIndex = (gatewayRef.variants || []).indexOf(v);
            return (
              <VariantCellCard
                key={v._id || `${refIndex}-${variantIndex}`}
                variant={v}
                refIndex={refIndex}
                variantIndex={variantIndex}
                gwColor={gwColor}
                isEditMode={isEditMode}
                onUpdateVariant={onUpdateVariant}
                onRemoveVariant={onRemoveVariant}
                basePrice={row.lv?.price}
                gatewayPercent={
                  (gatewayRef.pricePercent != null && Number(gatewayRef.pricePercent) !== 0)
                    ? gatewayRef.pricePercent
                    : (matchedGateway?.pricePercent ?? 0)
                }
                isGatewaySyncDisabled={isGatewaySyncDisabled}
                isProductSyncDisabled={isProductSyncDisabled}
                isLogicalVariantSyncDisabled={isLogicalVariantSyncDisabled}
              />
            );
          })}
          <Button
            size="xs"
            variant="ghost"
            colorScheme={gwColor}
            leftIcon={<AddIcon boxSize={2} />}
            onClick={() => onAddVariant(refIndex, logicalKey, row)}
          >
            +
          </Button>
        </VStack>
      ) : (
        <Flex h="full" minH="70px" align="center" justify="center">
          <Button
            size="xs"
            variant="ghost"
            color={mutedColor}
            leftIcon={<AddIcon boxSize={2} />}
            onClick={() => onAddVariant(refIndex, logicalKey, row)}
            _hover={{ color: 'teal.500', bg: 'teal.50' }}
          >
            اتصال
          </Button>
        </Flex>
      )}
    </Box>
  );
}

function LogicalVariantSpineCard({
  lv,
  lvIndex,
  isUnmappedRow,
  isProductLevel,
  isEditMode,
  onLogicalVariantField,
  onRemoveLogicalVariant,
}) {
  const rowBg = useColorModeValue(
    isProductLevel ? 'blue.50' : isUnmappedRow ? 'orange.50' : 'teal.50',
    isProductLevel ? 'blue.900' : isUnmappedRow ? 'orange.900' : 'whiteAlpha.100'
  );
  const rowBorder = useColorModeValue(
    isProductLevel ? 'blue.200' : isUnmappedRow ? 'orange.200' : 'teal.200',
    isProductLevel ? 'blue.700' : isUnmappedRow ? 'orange.700' : 'teal.700'
  );
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.800');

  return (
    <Box
      p={3}
      bg={rowBg}
      borderWidth="1.5px"
      borderColor={rowBorder}
      borderRadius="xl"
      borderRightWidth="4px"
      borderRightColor={isProductLevel ? 'blue.400' : isUnmappedRow ? 'orange.400' : 'teal.400'}
      h="full"
      w="full"
      minH="85px"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      boxShadow="2xs"
    >
      {isProductLevel ? (
        <>
          <HStack spacing={1.5} mb={1}>
            <Icon as={FaBox} color="blue.500" boxSize={3.5} />
            <Text fontSize="xs" fontWeight="bold" color={textColor}>سطح محصول</Text>
          </HStack>
          <Text fontSize="10px" color={mutedColor}>بدون تنوع منطقی</Text>
          <Text fontSize="sm" fontWeight="black" color="blue.500" mt={1}>
            {lv?.stock ?? 0} عدد
          </Text>
        </>
      ) : isUnmappedRow ? (
        <>
          <HStack spacing={1.5} mb={1}>
            <Icon as={FaUnlink} color="orange.500" boxSize={3.5} />
            <Text fontSize="xs" fontWeight="bold" color={textColor}>بدون اتصال (Unmapped)</Text>
          </HStack>
          <Text fontSize="10px" color={mutedColor} lineHeight="tall">
            تنوع‌های درگاه بدون اتصال به انبار مرکزی
          </Text>
        </>
      ) : (
        <>
          <Flex justify="space-between" align="center" mb={1.5}>
            <HStack spacing={1}>
              <Icon as={FaSitemap} color="teal.500" boxSize={3} />
              <Badge colorScheme="teal" fontSize="9px" borderRadius="md">
                تنوع {lvIndex + 1}
              </Badge>
            </HStack>
            <IconButton
              aria-label="حذف تنوع"
              icon={<DeleteIcon />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => onRemoveLogicalVariant(lvIndex)}
            />
          </Flex>
          <Input
            size="xs"
            fontWeight="bold"
            bg={inputBg}
            borderRadius="md"
            placeholder="کد تنوع (حسابداری)"
            value={lv.accountingCode || ''}
            onChange={(e) => onLogicalVariantField(lvIndex, 'accountingCode', e.target.value)}
            mb={1.5}
          />
          <Flex align="center" justify="space-between" gap={1} mb={1}>
            <Text fontSize="10px" color={mutedColor} fontWeight="medium">موجودی انبار:</Text>
            <NumberInput
              size="xs"
              min={0}
              w="75px"
              value={lv.stock}
              isDisabled={isEditMode}
              onChange={(_, val) => onLogicalVariantField(lvIndex, 'stock', val)}
            >
              <NumberInputField borderRadius="md" bg={inputBg} fontSize="xs" textAlign="center" />
            </NumberInput>
          </Flex>
          <Flex align="center" justify="space-between" gap={1}>
            <Text fontSize="10px" color={mutedColor} fontWeight="medium">قیمت پایه:</Text>
            <PriceInput
              value={lv.price}
              onChange={(val) => onLogicalVariantField(lvIndex, 'price', val)}
              bg={inputBg}
            />
          </Flex>
          <FormControl display="flex" alignItems="center" justifyContent="space-between" mt={1.5}>
            <Tooltip
              label="خاموش = قیمت این تنوع منطقی روی هیچ درگاهی اعمال نمی‌شود"
              fontSize="xs"
              placement="top"
              hasArrow
            >
              <FormLabel fontSize="9px" mb={0} color={mutedColor} cursor="help" textDecoration="underline dotted">
                قیمت در همه درگاه‌ها
              </FormLabel>
            </Tooltip>
            <Switch
              size="xs"
              colorScheme="teal"
              isChecked={lv.syncPrice !== false}
              onChange={(e) => onLogicalVariantField(lvIndex, 'syncPrice', e.target.checked)}
            />
          </FormControl>
        </>
      )}
    </Box>
  );
}

function HeaderCorner({ onAddLogicalVariant }) {
  return (
    <VStack align="start" justify="center" spacing={2} w="full" h="full" minH="64px">
      <HStack spacing={1.5}>
        <Icon as={FaSitemap} color="teal.500" boxSize={3.5} />
        <Text fontSize="xs" fontWeight="bold">تنوع‌های منطقی انبار</Text>
      </HStack>
      <Button
        size="xs"
        colorScheme="teal"
        variant="solid"
        leftIcon={<AddIcon boxSize={2.5} />}
        onClick={onAddLogicalVariant}
        w="full"
        borderRadius="md"
        shadow="xs"
      >
        افزودن تنوع جدید
      </Button>
    </VStack>
  );
}

function buildMatrixRows(logicalVariants, productStock, hasLogicalVariants) {
  if (hasLogicalVariants) {
    const rows = logicalVariants.map((lv, i) => ({
      key: resolveLogicalVariantKey(lv, i),
      lv,
      lvIndex: i,
      logicalKey: resolveLogicalVariantKey(lv, i),
      isUnmappedRow: false,
      isProductLevel: false,
    }));
    rows.push({
      key: 'unmapped',
      lv: null,
      lvIndex: -1,
      logicalKey: null,
      isUnmappedRow: true,
      isProductLevel: false,
    });
    return rows;
  }
  return [{
    key: 'product-level',
    lv: { accountingCode: 'موجودی محصول', stock: productStock },
    lvIndex: 0,
    logicalKey: null,
    isUnmappedRow: false,
    isProductLevel: true,
  }];
}

export default function ProductVariantOrgChart({
  productName,
  productStock,
  productSyncPrice = true,
  logicalVariants,
  gatewayRefs,
  gateways,
  isEditMode,
  onAddLogicalVariant,
  onRemoveLogicalVariant,
  onLogicalVariantField,
  onAddGatewayRef,
  onRemoveGatewayRef,
  onGatewayRefField,
  onAddVariant,
  onUpdateVariant,
  onRemoveVariant,
}) {
  const isProductSyncDisabled = productSyncPrice === false;
  const sectionBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const rootBg = useColorModeValue('blue.500', 'blue.600');
  const legendBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const scrollTrack = useColorModeValue('#e2e8f0', '#4a5568');
  const stickyBg = useColorModeValue('white', 'gray.800');

  const stats = useMemo(
    () => countMappedVariants(logicalVariants, gatewayRefs),
    [logicalVariants, gatewayRefs]
  );

  const hasLogicalVariants = logicalVariants.length > 0;
  const matrixRows = buildMatrixRows(logicalVariants, productStock, hasLogicalVariants);

  const handleAddVariant = (refIndex, logicalKey, row) => {
    const logicalVariantId = row.isUnmappedRow || row.isProductLevel ? '' : logicalKey;
    onAddVariant(refIndex, logicalVariantId);
  };

  const columnWidthPx = gatewayRefs.length > 6 ? 140 : 160;
  const columnWidth = `${columnWidthPx}px`;

  return (
    <Box maxW="100%" minW={0}>
      <style>{`
        .org-chart-grid-scroll {
          overflow: auto !important;
          min-width: 0;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }
        .org-chart-grid-scroll::-webkit-scrollbar { height: 8px; width: 8px; }
        .org-chart-grid-scroll::-webkit-scrollbar-track { background: ${scrollTrack}; border-radius: 8px; }
        .org-chart-grid-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 8px; }
      `}</style>

      {/* راهنما */}
      <Flex
        mb={4}
        p={3}
        bg={legendBg}
        borderRadius="xl"
        gap={4}
        flexWrap="wrap"
        fontSize="xs"
        color={mutedColor}
      >
        <HStack>
          <Box w={3} h={3} bg="blue.500" borderRadius="sm" />
          <Text>محصول (ریشه)</Text>
        </HStack>
        <HStack>
          <Box w={3} h={3} bg="teal.400" borderRadius="sm" />
          <Text>تنوع منطقی (انبار مرکزی)</Text>
        </HStack>
        <HStack>
          <Box w={3} h={3} bg="purple.400" borderRadius="sm" />
          <Text>سلول = اتصال به درگاه</Text>
        </HStack>
        <HStack>
          <Box w={3} h={3} bg="orange.400" borderRadius="sm" />
          <Text>ردیف بدون مپ</Text>
        </HStack>
        {isProductSyncDisabled && (
          <Badge colorScheme="purple" variant="solid" px={2} py={0.5} borderRadius="md" fontSize="10px">
            سیستم قیمت متناسب برای این محصول غیرفعال است
          </Badge>
        )}
        <Text mr="auto">
          <Icon as={FaLink} display="inline" mr={1} />
          {stats.mapped} متصل · {stats.unmapped} بدون مپ · {gatewayRefs.length} درگاه
        </Text>
      </Flex>

      <Box
        bg={sectionBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={{ base: 4, md: 6 }}
        maxW="100%"
        minW={0}
        overflow="hidden"
      >
        {/* سطح ۰: ریشه محصول */}
        <Flex direction="column" align="center" mb={2}>
          <Box
            px={6}
            py={3}
            bg={rootBg}
            color="white"
            borderRadius="2xl"
            boxShadow="lg"
            textAlign="center"
            minW="200px"
            maxW="400px"
          >
            <HStack justify="center" spacing={2} mb={1}>
              <Icon as={FaBox} />
              <Text fontWeight="black" fontSize="md" isTruncated>
                {productName || 'محصول'}
              </Text>
            </HStack>
            {!hasLogicalVariants && (
              <Badge colorScheme="whiteAlpha" fontSize="10px">
                موجودی: {productStock ?? 0}
              </Badge>
            )}
          </Box>
          <ConnectorDown height={hasLogicalVariants ? 28 : 16} />
          {hasLogicalVariants && <ConnectorBus childCount={logicalVariants.length} />}
        </Flex>

        {/* ماتریس چارت سازمانی — طراحی یکپارچه سطری با Sticky Column */}
        {gatewayRefs.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={12}
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={borderColor}
            borderRadius="xl"
          >
            <Icon as={FaStore} boxSize={10} color="gray.300" mb={3} />
            <Text color={mutedColor} fontSize="sm" mb={3}>
              هنوز درگاهی اضافه نشده — ستون‌های درگاه اینجا نمایش داده می‌شوند
            </Text>
            <Button colorScheme="purple" leftIcon={<FaStore />} onClick={onAddGatewayRef}>
              افزودن اولین درگاه
            </Button>
          </Flex>
        ) : (
          <Box
            maxW="100%"
            minW={0}
            w="100%"
            maxH={MATRIX_MAX_HEIGHT}
            overflow="auto"
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            bg={sectionBg}
            className="org-chart-grid-scroll custom-scrollbar"
            dir="rtl"
            position="relative"
          >
            {/* ردیف هدر چسبان (Sticky Header) */}
            <Flex
              position="sticky"
              top={0}
              zIndex={4}
              bg={stickyBg}
              borderBottomWidth="2px"
              borderColor={borderColor}
              minW="max-content"
              boxShadow="sm"
            >
              {/* گوشه چسبان بالا-راست (تنوع / درگاه) */}
              <Box
                w={LEFT_COL_W}
                minW={LEFT_COL_W}
                maxW={LEFT_COL_W}
                flexShrink={0}
                position="sticky"
                right={0}
                zIndex={5}
                bg={stickyBg}
                p={2.5}
                borderLeftWidth="2px"
                borderColor={borderColor}
                boxShadow="inset -1px 0 0 rgba(0, 0, 0, 0.05), -4px 0 6px -2px rgba(0, 0, 0, 0.08)"
              >
                <HeaderCorner onAddLogicalVariant={onAddLogicalVariant} />
              </Box>

              {/* ستون‌های هدر درگاه‌ها */}
              <Flex flex={1} gap={COL_GAP} p={2.5} align="stretch">
                {gatewayRefs.map((gatewayRef, refIndex) => (
                  <GatewayHeader
                    key={gatewayRef?._id || refIndex}
                    refIndex={refIndex}
                    gatewayRef={gatewayRef}
                    gateways={gateways}
                    onGatewayRefField={onGatewayRefField}
                    onRemoveGatewayRef={onRemoveGatewayRef}
                    columnWidth={columnWidth}
                    isProductSyncDisabled={isProductSyncDisabled}
                  />
                ))}
                <Flex
                  minW={columnWidth}
                  minH="64px"
                  align="center"
                  justify="center"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={borderColor}
                  borderRadius="xl"
                  cursor="pointer"
                  flexShrink={0}
                  bg={stickyBg}
                  onClick={onAddGatewayRef}
                  _hover={{ borderColor: 'purple.400', bg: 'purple.50' }}
                  transition="all 0.2s"
                >
                  <VStack spacing={1}>
                    <Icon as={FaStore} color="purple.500" boxSize={4} />
                    <Text fontSize="xs" color="purple.600" fontWeight="bold">+ درگاه جدید</Text>
                  </VStack>
                </Flex>
              </Flex>
            </Flex>

            {/* بدنه ماتریس — هر ردیف کاملاً هم‌راستا و کشیده (Stretch) */}
            <Box minW="max-content">
              {matrixRows.map((row) => (
                <Flex
                  key={row.key}
                  align="stretch"
                  minW="max-content"
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                  _hover={{ bg: rowHoverBg }}
                  transition="background 0.15s ease"
                >
                  {/* ستون چسبان تنوع منطقی در سمت راست */}
                  <Box
                    w={LEFT_COL_W}
                    minW={LEFT_COL_W}
                    maxW={LEFT_COL_W}
                    flexShrink={0}
                    position="sticky"
                    right={0}
                    zIndex={2}
                    bg={stickyBg}
                    p={2.5}
                    borderLeftWidth="2px"
                    borderColor={borderColor}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    boxShadow="inset -1px 0 0 rgba(0, 0, 0, 0.05), -4px 0 6px -2px rgba(0, 0, 0, 0.08)"
                  >
                    <LogicalVariantSpineCard
                      lv={row.lv}
                      lvIndex={row.lvIndex}
                      isUnmappedRow={row.isUnmappedRow}
                      isProductLevel={row.isProductLevel}
                      isEditMode={isEditMode}
                      onLogicalVariantField={onLogicalVariantField}
                      onRemoveLogicalVariant={onRemoveLogicalVariant}
                    />
                  </Box>

                  {/* سلول‌های درگاه‌های این ردیف */}
                  <Flex flex={1} gap={COL_GAP} p={2.5} align="stretch">
                    {gatewayRefs.map((gatewayRef, refIndex) => {
                      const meta = getGatewayMeta(gatewayRef, gateways);
                      return (
                        <MatrixCell
                          key={gatewayRef?._id || refIndex}
                          refIndex={refIndex}
                          gatewayRef={gatewayRef}
                          logicalKey={row.logicalKey}
                          row={row}
                          gwColor={meta.color}
                          isEditMode={isEditMode}
                          onAddVariant={handleAddVariant}
                          onUpdateVariant={onUpdateVariant}
                          onRemoveVariant={onRemoveVariant}
                          columnWidth={columnWidth}
                          gateways={gateways}
                          isProductSyncDisabled={isProductSyncDisabled}
                        />
                      );
                    })}
                    <Box minW={columnWidth} flexShrink={0} />
                  </Flex>
                </Flex>
              ))}
            </Box>
          </Box>
        )}
        {gatewayRefs.length > 0 && (
          <Text fontSize="10px" color={mutedColor} mt={2.5} textAlign="center">
            ستون تنوع منطقی در اسکرول افقی ثابت است · هدر درگاه‌ها در اسکرول عمودی ثابت می‌ماند · ارتفاع تمام سلول‌های هر ردیف کاملاً هم‌راستا است
          </Text>
        )}
      </Box>
    </Box>
  );
}
